import { supabase } from '../lib/supabase';

export const subscriptionService = {
  // Get all subscription plans
  async getPlans() {
    const { data, error } = await supabase?.from('subscription_plans')?.select('*')?.eq('is_active', true)?.order('base_price', { ascending: true });

    if (error) throw error;
    return data;
  },

  // Get user's current subscription
  async getCurrentSubscription(userId) {
    const { data, error } = await supabase?.from('user_subscriptions')?.select(`*,plan:subscription_plans(*)`)?.eq('user_id', userId)?.eq('status', 'active')?.order('created_at', { ascending: false })?.limit(1)?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    return data;
  },

  // Get user's subscription tier
  async getUserTier(userId) {
    const { data, error } = await supabase?.rpc('get_user_subscription_tier', { user_uuid: userId });

    if (error) throw error;
    return data || 'free';
  },

  // Check feature access
  async hasFeatureAccess(userId, featureKey) {
    const { data, error } = await supabase?.rpc('has_feature_access', { 
        user_uuid: userId, 
        feature_key_param: featureKey 
      });

    if (error) throw error;
    return data;
  },

  // Get all feature flags
  async getFeatureFlags() {
    const { data, error } = await supabase?.from('feature_flags')?.select('*')?.eq('is_enabled', true)?.order('required_tier', { ascending: true });

    if (error) throw error;
    return data;
  },

  // Get usage metrics for current period
  async getCurrentUsage(userId, metricType) {
    const { data, error } = await supabase?.rpc('get_current_usage', { 
        user_uuid: userId, 
        metric_type_param: metricType 
      });

    if (error) throw error;
    return data || 0;
  },

  // Get all usage metrics for user
  async getAllUsageMetrics(userId) {
    const subscription = await this.getCurrentSubscription(userId);
    if (!subscription) return {};

    const periodStart = new Date(subscription.current_period_start);
    
    const { data, error } = await supabase?.from('usage_metrics')?.select('*')?.eq('user_id', userId)?.gte('period_start', periodStart?.toISOString())?.order('created_at', { ascending: false });

    if (error) throw error;

    // Aggregate by metric type
    const aggregated = {};
    data?.forEach(metric => {
      if (!aggregated?.[metric?.metric_type]) {
        aggregated[metric.metric_type] = 0;
      }
      aggregated[metric.metric_type] += metric?.quantity;
    });

    return aggregated;
  },

  // Track usage
  async trackUsage(userId, metricType, quantity = 1) {
    const subscription = await this.getCurrentSubscription(userId);
    const periodStart = subscription?.current_period_start || new Date()?.toISOString();
    const periodEnd = subscription?.current_period_end || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString();

    const { data, error } = await supabase?.from('usage_metrics')?.insert({
        user_id: userId,
        subscription_id: subscription?.id,
        metric_type: metricType,
        quantity,
        period_start: periodStart,
        period_end: periodEnd
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Create payment intent
  async createPaymentIntent(planId, userId, customerInfo) {
    const { data, error } = await supabase?.functions?.invoke('create-payment-intent', {
      body: { planId, userId, customerInfo }
    });

    if (error) throw error;
    return data;
  },

  // Confirm subscription after payment
  async confirmSubscription(paymentIntentId, planId, userId) {
    const { data, error } = await supabase?.functions?.invoke('confirm-subscription', {
      body: { paymentIntentId, planId, userId }
    });

    if (error) throw error;
    return data;
  },

  // Get payment history
  async getPaymentHistory(userId) {
    const { data, error } = await supabase?.from('payment_transactions')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Get payment methods
  async getPaymentMethods(userId) {
    const { data, error } = await supabase?.from('payment_methods')?.select('*')?.eq('user_id', userId)?.order('is_default', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Cancel subscription
  async cancelSubscription(subscriptionId) {
    const { data, error } = await supabase?.from('user_subscriptions')?.update({
        cancel_at_period_end: true,
        canceled_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      })?.eq('id', subscriptionId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Format currency
  formatCurrency(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    })?.format(amount);
  },

  // Get plan limits
  getPlanLimits(plan) {
    if (!plan?.limits) return {};
    return plan?.limits;
  },

  // Check if usage exceeds limit
  isOverLimit(currentUsage, limit) {
    if (limit === -1) return false; // Unlimited
    return currentUsage >= limit;
  },

  // Calculate overage cost
  calculateOverageCost(currentUsage, includedQuantity, overagePrice) {
    if (currentUsage <= includedQuantity) return 0;
    const overage = currentUsage - includedQuantity;
    return overage * overagePrice;
  }
};